/**
 * uiClickSounds — one global listener that gives every clickable UI element a
 * click sound, sourced from the active GameTheme.
 *
 * Why a delegator and not per-button wiring: the UI is ~24 semantic <button>s
 * but ~150 styled <div>s with click handlers. Listening once at the document
 * (capture phase) and resolving the nearest *clickable* element covers them all.
 * The "this is clickable" signal is `cursor: pointer` (every div-button sets it)
 * plus the semantic interactive elements.
 *
 * Which control plays which sound is DECLARED per element, not detected. The old
 * heuristic (guess "button vs tap" from tag/class/text) was inconsistent on the
 * same UI, so it's gone. The role is read straight off `data-sound`:
 *   - data-sound="button"        → the theme's button press (heftier).
 *   - data-sound="tap"           → the theme's tap click (lighter).
 *   - data-sound="SOME_SOUND_ID" → that specific SoundId.
 *   - data-sound="none"          → silent (opt this control out).
 *   - (no attribute)             → DEFAULT: tap. Tag the button-press controls
 *                                  explicitly; everything else is a tap.
 *   - [disabled] / aria-disabled → silent.
 *
 * Both `button` and `tap` resolve through the active theme, so a seasonal theme
 * still re-skins them for free.
 *
 * Silent-safe: sound.play is a no-op until the catalog is ready, so installing
 * this early is harmless.
 */

import { sound, SoundId } from '../engine/audio/sound';
import { getActiveTheme } from './theme/GameTheme';

/** Semantic elements that are clickable regardless of cursor styling. */
const SEMANTIC_CLICKABLE =
  'button,a[href],[role="button"],[role="tab"],[role="menuitem"],' +
  'input[type="button"],input[type="submit"],input[type="checkbox"],' +
  'input[type="radio"],select,summary,label[for],[data-action],[data-sound]';

/** How far up the ancestor chain we look for a clickable element. */
const MAX_WALK = 6;

let installed = false;

/** Install the global click-sound delegator. Idempotent; call once at boot. */
export function installUiClickSounds(): void {
  if (installed) return;
  installed = true;
  // Capture phase so we still hear the click even if a bubble-phase handler
  // calls stopPropagation (some controls do, to scope their own logic).
  document.addEventListener('click', onClick, { capture: true });
}

function onClick(e: MouseEvent): void {
  const target = e.target as HTMLElement | null;
  if (!target) return;

  const el = findClickable(target);
  if (!el) return;
  if (isDisabled(el)) return;

  const theme = getActiveTheme().sounds;
  // The nearest ancestor (or the element itself) carrying data-sound declares
  // the role; without one, the default is a tap.
  const decl = el.closest('[data-sound]') as HTMLElement | null;
  const role = decl?.getAttribute('data-sound');

  if (role === 'none') return;
  if (role === 'button') { sound.play(theme.button ?? theme.click); return; }
  if (role === 'tap') { sound.play(theme.click); return; }
  if (role) {
    // A literal SoundId, e.g. data-sound="WALLET_OPEN".
    const id = (SoundId as Record<string, SoundId>)[role];
    if (id) { sound.play(id); return; }
    // Unknown value: fall through to the default rather than going silent.
  }

  // No declared role → tap (the deterministic default; button presses opt in).
  sound.play(theme.click);
}

/**
 * Walk up from the event target to the nearest element that reads as clickable:
 * a semantic interactive element, or anything with `cursor: pointer` (the
 * codebase's div-button convention). Returns null for plain content clicks.
 */
function findClickable(start: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = start;
  for (let i = 0; el && i < MAX_WALK && el !== document.body; i++) {
    if (el.matches(SEMANTIC_CLICKABLE)) return el;
    // getComputedStyle is only hit on real clicks (rare), so the cost is fine.
    if (getComputedStyle(el).cursor === 'pointer') return el;
    el = el.parentElement;
  }
  return null;
}

/** Disabled controls make no sound. */
function isDisabled(el: HTMLElement): boolean {
  if (el.getAttribute('aria-disabled') === 'true') return true;
  if ((el as HTMLButtonElement).disabled) return true;
  return el.closest('[disabled],[aria-disabled="true"]') !== null;
}
